import { useState, useEffect, type ReactElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button } from '../components/ui';
import { ApiError } from '../lib/api';
import {
  getMatchClaim,
  expressInterest,
  declineMatch,
  type PublicMatchView,
} from '../lib/match-claim';

function formatSalary(view: PublicMatchView): string | null {
  const sym = view.salary_currency === 'USD' ? '$' : view.salary_currency === 'EUR' ? '€' : '£';
  const k = (n: number) => `${sym}${Math.round(n / 1000)}k`;
  if (view.salary_min && view.salary_max) return `${k(view.salary_min)}–${k(view.salary_max)}`;
  if (view.salary_min) return `${k(view.salary_min)}+`;
  return null;
}

/**
 * Unauthenticated landing for a recruiter's match-request invite. The candidate
 * sees the matched role before signing up and can express interest or decline.
 */
export default function MatchClaimPage(): ReactElement {
  const { token = '' } = useParams();
  const [view, setView] = useState<PublicMatchView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<'idle' | 'interested' | 'declined'>('idle');
  // Action errors are kept separate from the page-load `error` so a failed
  // interest/decline POST shows inline (and stays retryable) rather than
  // replacing the invite with the invalid/expired page.
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getMatchClaim(token)
      .then((v) => {
        if (!active) return;
        setView(v);
        if (v.state === 'interested' || v.state === 'completed') setAction('interested');
        if (v.state === 'declined') setAction('declined');
      })
      .catch(() => active && setError('This invitation link is invalid or has expired.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const onInterested = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await expressInterest(token);
      setAction('interested');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404 && error.code === 'JOB_UNAVAILABLE') {
        setView((current) => current ? { ...current, can_show_interest: false } : current);
      } else {
        setActionError('Something went wrong — please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const onDecline = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await declineMatch(token);
      setAction('declined');
    } catch {
      setActionError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl items-center px-4 py-16 sm:px-6">
        <Card variant="elevated" className="w-full text-center">
          <p className="text-text-secondary">Loading…</p>
        </Card>
      </div>
    );
  }
  if (error || !view) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl items-center px-4 py-16 sm:px-6">
        <Card variant="elevated" className="w-full text-center">
          <p className="text-text-primary">{error ?? 'Invitation not found.'}</p>
          <Link to="/" className="mt-4 inline-block font-semibold text-brand-carnaby-pink hover:underline">
            Browse open roles
          </Link>
        </Card>
      </div>
    );
  }

  const salary = formatSalary(view);
  const place = view.location ?? (view.remote_allowed ? 'Remote' : null);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl items-center px-4 py-16 sm:px-6">
      <Card variant="elevated" className="w-full">
        <p className="text-xs font-bold uppercase tracking-widest text-brand-carnaby-pink">You&apos;ve been matched</p>
        <h1 className="mt-2 text-3xl font-bold text-text-primary">{view.job_title}</h1>
        <p className="mt-2 text-text-secondary">
          {[view.industry, place, salary].filter(Boolean).join(' · ')}
        </p>

        {action === 'interested' ? (
          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <p className="font-semibold text-emerald-800">Great — we&apos;ve let the recruiter know you&apos;re interested.</p>
            <p className="mt-1 text-sm text-emerald-700">
              Create your profile to share your details and move forward.
            </p>
            <Link
              to="/signup"
              className="mt-4 inline-block rounded-full bg-brand-carnaby-pink px-6 py-3 font-bold text-white hover:bg-brand-dark-carnaby-pink"
            >
              Create my profile
            </Link>
          </div>
        ) : action === 'declined' ? (
          <div className="mt-8">
            <p className="text-text-primary">No problem — thanks for letting us know.</p>
            <Link to="/" className="mt-6 inline-block font-semibold text-brand-carnaby-pink hover:underline">
              Browse all open roles
            </Link>
          </div>
        ) : !view.can_show_interest ? (
          <div className="mt-8">
            <p role="status" className="text-text-primary">This role is no longer available.</p>
            <Link to="/" className="mt-4 inline-block font-semibold text-brand-carnaby-pink">Browse open roles</Link>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button label="I'm interested" onClick={onInterested} disabled={busy} />
              <Button label="Not for me" variant="secondary" onClick={onDecline} disabled={busy} />
            </div>
            {actionError ? (
              <p className="mt-3 text-sm font-medium text-rose-600" role="alert">{actionError}</p>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
